import React, { createContext, useState, useContext, useMemo } from "react";
import PropTypes from "prop-types";
import { createMuiTheme, ThemeProvider as MUIThemeProvider } from "@material-ui/core/styles";
import { CssBaseline } from "@material-ui/core";
import { brand } from "../../styles/brand";

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [darkMode, setDarkMode] = useState(false);

  const toggleTheme = () => {
    setDarkMode((prevMode) => !prevMode);
  };

  const theme = useMemo(
    () =>
      createMuiTheme({
        palette: {
          type: darkMode ? "dark" : "light",
          primary: {
            main: brand.blue,
            light: brand.sky,
            dark: brand.navy,
            contrastText: "#FFFFFF",
          },
          secondary: {
            main: brand.orange,
          },
          success: {
            main: brand.success,
          },
          background: darkMode
            ? { default: "#071D2C", paper: "#0E2B3E" }
            : { default: brand.surface, paper: "#FFFFFF" },
          text: darkMode
            ? { primary: "#F4F8FB", secondary: "#AFC0CB" }
            : { primary: brand.text, secondary: brand.muted },
        },
        typography: {
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          h1: { fontWeight: 800 },
          h2: { fontWeight: 800 },
          h3: { fontWeight: 700 },
          h4: { fontWeight: 700 },
          h5: { fontWeight: 700 },
          h6: { fontWeight: 700 },
          button: { fontWeight: 700, textTransform: "none", letterSpacing: 0 },
        },
        shape: {
          borderRadius: 14,
        },
        scrollbarStyles: {
          "&::-webkit-scrollbar": {
            width: "7px",
            height: "7px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: darkMode ? "#395568" : "#C8D9E4",
            borderRadius: "10px",
          },
        },
        overrides: {
          MuiCssBaseline: {
            "@global": {
              "html, body, #root": { minHeight: "100%" },
              body: { margin: 0, backgroundColor: darkMode ? "#071D2C" : brand.surface },
              "*": { boxSizing: "border-box" },
            },
          },
          MuiButton: {
            root: {
              borderRadius: 11,
              minHeight: 38,
              paddingLeft: 18,
              paddingRight: 18,
            },
            contained: {
              boxShadow: "0 7px 18px rgba(8, 105, 153, 0.16)",
            },
          },
          MuiPaper: {
            rounded: { borderRadius: 18 },
            elevation1: {
              boxShadow: darkMode
                ? "0 10px 28px rgba(0, 0, 0, 0.28)"
                : "0 18px 44px rgba(23, 63, 88, 0.09)",
            },
          },
          MuiOutlinedInput: {
            root: { borderRadius: 11 },
          },
          MuiDialog: {
            paper: { borderRadius: 20 },
          },
          MuiMenu: {
            paper: { borderRadius: 12, boxShadow: "0 18px 44px rgba(23,63,88,.14)" },
          },
          MuiTableCell: {
            head: {
              color: darkMode ? "#EAF3F8" : brand.navy,
              fontWeight: 700,
              backgroundColor: darkMode ? "#123448" : "#F1F7FA",
            },
            root: {
              borderBottomColor: darkMode ? "rgba(255,255,255,.08)" : "#E6EEF3",
            },
          },
          MuiDialogTitle: {
            root: { color: darkMode ? "#FFFFFF" : brand.navy },
          },
        },
      }),
    [darkMode]
  );

  const contextValue = useMemo(() => ({ darkMode, toggleTheme }), [darkMode]);

  return (
    <ThemeContext.Provider value={contextValue}>
      <MUIThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MUIThemeProvider>
    </ThemeContext.Provider>
  );
};
ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useThemeContext = () => useContext(ThemeContext);
